import axios from 'axios';

const api = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api' });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ps_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const login = (email: string, password: string) => api.post('/auth/login', { email, password });
export const getAdAccounts = () => api.get('/meta/ad-accounts');
export const getTokenStatus = () => api.get('/meta/token-status');
export const updateToken = (token: string) => api.post('/meta/update-token', { token });
export const getAccountDetail = (accountId: string) => api.get(`/meta/account/${accountId}`);
export const getPerformance = (accountIds?: string[]) => {
  const params = accountIds?.length ? `?account_ids=${accountIds.join(',')}` : '';
  return api.get(`/meta/performance${params}`);
};

export const getPerformanceByCampaign = (pattern: string, accountIds: string[]) =>
  api.get(`/meta/performance/by-campaign?pattern=${encodeURIComponent(pattern)}&account_ids=${accountIds.join(',')}`);


export const getCustomerSettings = () => api.get('/push/customer-settings');

export const getTemplates = () => api.get('/templates/');
export const createTemplate = (data: any) => api.post('/templates/', data);
export const updateTemplateApi = (id: string, data: any) => api.put(`/templates/${id}`, data);
export const deleteTemplateApi = (id: string) => api.delete(`/templates/${id}`);
export const getPixels = (accountId: string) => api.get(`/push/pixels/${accountId}`);
export const getPixelsBulk = (accountIds: string[]) => api.post('/push/pixels/bulk', { account_ids: accountIds });
export const getPages = () => api.get('/push/pages');
export const getPagesForAccount = (accountId: string) => api.get(`/push/pages/${accountId}`);
export const getPushCampaigns = (accountId: string) => api.get(`/push/campaigns/${accountId}`);
export const getCampaignStructure = (campaignId: string) => api.get(`/push/campaign-structure/${campaignId}`);
export const searchLocation = (q: string) => api.get(`/push/search-location?q=${encodeURIComponent(q)}`);
export const startPush = (payload: any) => api.post('/push/start', payload);
export const getPushLogs = (jobId: string, offset: number) => api.get(`/push/logs/${jobId}?offset=${offset}`);
export const startNewCampaign = (payload: any) => api.post('/push/start-new', payload);
export const SSE_BASE = process.env.NODE_ENV === 'production' ? '/api/push/stream' : 'http://localhost:5000/api/push/stream';
export const updateAccountSettings = (accountId: string, data: any) => api.put(`/meta/account/${accountId}/settings`, data);
export const getPushHistory = (accountId: string) => api.get(`/push/history/${accountId}`);
export const getWinningAds = (campaignIds: string[]) => api.post('/meta/winning-ads', { campaign_ids: campaignIds });
export const getAdAnalysis = (pattern: string, range: string = 'maximum') =>
  api.get(`/meta/ad-analysis?pattern=${encodeURIComponent(pattern)}&range=${encodeURIComponent(range)}`);
export const getAdTexts = (accountId: string) => api.get(`/push/ad-texts/${accountId}`);
export const updateAdText = (data: any) => api.post('/push/update-ad-text', data);

// ── Assets ─────────────────────────────────────────────────────────────────────
export const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';

export const getMarke = (accountId: string) => api.get(`/assets/kunden/${accountId}/marke`);
export const saveMarke = (accountId: string, data: { primary: string; secondary: string; accent: string }) =>
  api.post(`/assets/kunden/${accountId}/marke`, data);

export const uploadLogo = (accountId: string, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/assets/kunden/${accountId}/logo`, form);
};
export const deleteLogo = (accountId: string) => api.delete(`/assets/kunden/${accountId}/logo`);

export const getFotos = (accountId: string) => api.get(`/assets/kunden/${accountId}/fotos`);
export const uploadFotos = (accountId: string, files: File[]) => {
  const form = new FormData();
  files.forEach(f => form.append('files', f));
  return api.post(`/assets/kunden/${accountId}/fotos`, form);
};
export const deleteFoto = (accountId: string, filename: string) =>
  api.delete(`/assets/kunden/${accountId}/fotos/${filename}`);

export const getMasterSets = () => api.get('/assets/master');
export const uploadMasterImage = (setName: string, idx: number, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/assets/master/${setName}/image/${idx}`, form);
};

export const getVariationen = (accountId: string) => api.get(`/assets/kunden/${accountId}/variationen`);
export const uploadVariation = (accountId: string, setName: string, files: File[]) => {
  const form = new FormData();
  files.forEach(f => form.append('files', f));
  return api.post(`/assets/kunden/${accountId}/variationen/${setName}`, form);
};
export const replaceVariationImage = (accountId: string, setName: string, idx: number, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/assets/kunden/${accountId}/variationen/${setName}/${idx}`, form);
};

// ── Settings ───────────────────────────────────────────────────────────────────
export const getSettings = () => api.get('/settings/');
export const saveSettings = (data: Record<string, string>) => api.post('/settings/', data);

// ── Higgsfield ─────────────────────────────────────────────────────────────────
export const getHiggsfieldModels = () => api.get('/higgsfield/models');
export const generateHighgsfield = (data: FormData) => api.post('/higgsfield/generate', data);

// ── Ad-Test-Agent ────────────────────────────────────────────────────────────────
export const getAdAgentHealth = () => api.get('/adagent/health');
export const startAdBatch = (payload: { offer: string; count: number; brief?: string; brand_context?: string }) =>
  api.post('/adagent/batches', payload);
export const adAgentStreamUrl = (jobId: string) => `${API_BASE}/api/adagent/stream/${jobId}`;
export const listAdBatches = () => api.get('/adagent/batches');
export const getAdBatch = (batchId: string) => api.get(`/adagent/batches/${batchId}`);
export const approveAdBatch = (batchId: string) => api.post(`/adagent/batches/${batchId}/approve`);
export const approveAd = (adId: string) => api.post(`/adagent/ads/${adId}/approve`);
export const rejectAd = (adId: string) => api.post(`/adagent/ads/${adId}/reject`);
export const adImageUrl = (adId: string) => `${API_BASE}/api/adagent/ads/${adId}/image`;
export const exportAdBatchUrl = (batchId: string) => `${API_BASE}/api/adagent/batches/${batchId}/export`;
export const searchTrendtrack = (q: string) => api.get(`/adagent/trendtrack/search?q=${encodeURIComponent(q)}`);
export const getSwipes = () => api.get('/adagent/swipes');
export const addSwipe = (data: any) => api.post('/adagent/swipes', data);
export const deleteSwipe = (id: string) => api.delete(`/adagent/swipes/${id}`);

// ── WordPress / FluentForms ──────────────────────────────────────────────────────
export const getWpConfig = (accountId: string) => api.get(`/wp/config/${accountId}`);
export const saveWpConfig = (accountId: string, data: { wp_url?: string; wp_user?: string; wp_app_password?: string }) =>
  api.put(`/wp/config/${accountId}`, data);
export const testWpConnection = (accountId: string) => api.post(`/wp/test/${accountId}`);
export const importWpLeads = (accountId: string) => api.post(`/wp/import/${accountId}`);
export const getWpStatus = () => api.get('/wp/status');
export const testAllWp = () => api.post('/wp/test-all');
export const importAllWp = () => api.post('/wp/import-all');
export const detectWpForm = (url: string) => api.post('/wp/detect-form', { url });
export const mapWpForms = (accountId: string) => api.post(`/wp/map-forms/${accountId}`);
export const autodetectWp = (accountId: string) => api.post(`/wp/autodetect/${accountId}`);
export const getRealLeads = (accountId: string) => api.get(`/wp/leads/${accountId}`);
export const getRealLeadsAgg = (accountIds: string[], pattern: string, range: string) =>
  api.post('/wp/real-leads-agg', { account_ids: accountIds, pattern, range });

// ── Watchlist ────────────────────────────────────────────────────────────────────
export const getWatchlist = () => api.get('/watchlist/');
export const addToWatchlist = (accountId: string, name: string) => api.post('/watchlist/', { account_id: accountId, name });
export const removeFromWatchlist = (accountId: string) => api.delete(`/watchlist/${accountId}`);
export const setWatchlistCampaignVisibility = (accountId: string, campaignId: string, hidden: boolean) =>
  api.post(`/watchlist/${accountId}/visibility`, { campaign_id: campaignId, hidden });
export const getWatchlistCampaigns = (accountId: string) => api.get(`/watchlist/${accountId}/campaigns`);

export default api;
